import camelCase from 'lodash-es/camelCase'
import get from 'lodash-es/get'
import kebabCase from 'lodash-es/kebabCase'
import snakeCase from 'lodash-es/snakeCase'
import startCase from 'lodash-es/startCase'
import upperFirst from 'lodash-es/upperFirst'

import { ABBREVIATIONS } from '/@/constants/string'
import { isEmpty } from '/@/utils/is'

export type TextStyle =
  | 'camel'
  | 'pascal'
  | 'kebab'
  | 'snake'
  | 'sentence'
  | 'title'
  | undefined

/**
 * Python-like string format function
 * @param str - Template string.
 * @param data - Data to insert strings from.
 * @returns The formatted text.
 */
export function formatString(str: string, data: any) {
  return str.replace(/{([^{}]+)}/g, (match: string, val: string) => {
    let prop = data
    val.split('.').forEach((key: string) => {
      prop = prop[key]
    })

    return prop
  })
}

/**
 *@description Generates a two-character answer by taking the first letters of the given word string
 *
 * @example 'John Doe' -> 'JD'
 * @example 'John' -> 'JO'
 * @example 'John Doe Smith' -> 'JS'
 * @example 'John Doe Smith Muffin' -> 'JM'
 *
 * @param name
 */
export function getNameInitials(name: string): string {
  if (!name) return ''

  const uppercaseName = name.toUpperCase()
  // eslint-disable-next-line lodash/prefer-includes
  const hasTokens = uppercaseName.indexOf(' ') !== -1
  return (
    uppercaseName.substring(0, hasTokens ? 1 : 2) +
    (hasTokens ? uppercaseName.charAt(uppercaseName.lastIndexOf(' ') + 1) : '')
  )
}

/**
 *@description Get first letter of each Word in a String
 *
 * @example 'John Doe' -> 'JD'
 * @example 'John' -> 'JO'
 * @example 'John Doe Smith' -> 'JDS'
 * @example 'John Doe Smith Muffin' -> 'JDSM'
 *
 * @param string
 */
export function getFirstLetters(string: string): string {
  return string
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
}

/**
 * Convert string to boolean value.
 * @returns {boolean}
 * @param value - String value.
 */
export function parseBool(value: string): boolean {
  switch (value?.toLowerCase()?.trim()) {
    case 'true':
    case 'yes':
    case '1':
      return true

    case 'false':
    case 'no':
    case '0':
    case null:
    case undefined:
      return false

    default:
      return JSON.parse(value)
  }
}

/**
 * @method checkEmptyData
 * @description Does check for N/A or empty state label required fields.
 * @param row {Recordable} Holds row data.
 * @param item {string} Holds data key.
 * @param message {string} Holds empty state message.
 */
export function checkEmptyData(
  row: Recordable,
  item: string = '',
  message: string = 'N/A',
): string {
  const value = get(row, item)

  if (isEmpty(value)) return message
  return value
}

/**
 * It breaks a content according to the specified number of words and moves to the new line.
 * @param sentence
 * @param n - Every line's word count
 * @example
 * addNewLinesEveryNWords('Lorem ipsum dolor sit amet, consectetur adipiscing elit.', 3)
 *
 * Output:
 *  Lorem ipsum dolor
 *  sit amet, consectetur
 *  adipiscing elit.
 */
export function addNewLinesEveryNWords(sentence: string, n: number = 1) {
  // To-do -> adding more special characters
  const specialChars = ['&']
  let newSentence = ''

  // If there is a special character,
  // it adds that special character to the previous word.
  const wordMapper = (word: string, index: number, arr: string[]) => {
    const nextWord = arr[index + 1]
    const isNextWordExist = index < arr.length - 1

    if (isNextWordExist && specialChars.includes(nextWord)) {
      return `${word} ${nextWord}`
    }
    return word
  }

  // Exclude special characters from words
  const filterByExcludedSpecialChars = (word: string) => !specialChars.includes(word)

  const words = sentence.split(/\s+/).map(wordMapper).filter(filterByExcludedSpecialChars)

  words.forEach((word, index) => {
    const isSentenceResumeDividable = (index + 1) % n === 0
    newSentence += `${word} `

    if (isSentenceResumeDividable) {
      newSentence += `\n`
    }
  })

  return newSentence
}

/**
 * @method generateUniqueFieldId
 * @description Generates very unique ids for form fields.
 */
export function generateUniqueFieldId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2)
}

/**
 * @method detectTextStyle
 * @description Detects the text style of a given input string.
 * @param input - The input string to analyze.
 * @example
 * detectTextStyle('camelCaseExample') // 'camel'
 * detectTextStyle('PascalCaseExample') // 'pascal'
 * detectTextStyle('kebab-case-example') // 'kebab'
 * detectTextStyle('snake_case_example') // 'snake'
 * detectTextStyle('sentence case example') // 'sentence'
 * detectTextStyle('Title Case Example') // 'title'
 */
export function detectTextStyle(input: string): TextStyle {
  if (/^[a-z]+([A-Z][a-z]*)*$/.test(input)) return 'camel'
  if (/^[A-Z][a-z]+([A-Z][a-z]*)*$/.test(input)) return 'pascal'
  if (/^[a-z]+(-[a-z]+)*$/.test(input)) return 'kebab'
  if (/^[a-z]+(_[a-z]+)*$/.test(input)) return 'snake'
  if (/^[a-z]+(\s[a-z]+)*$/.test(input)) return 'sentence'
  if (/^[A-Z][a-z]+(\s[A-Z][a-z]+)*$/.test(input)) return 'title'
}

/**
 * @name convertTextStyle
 * @description Converts the text style of a given input string to a specified style.
 * This function consolidates all text case conversion utilities into a single function.
 *
 * @param input - The string to convert
 * @param toStyle - The desired output style ('camel', 'pascal', 'kebab', 'snake', 'sentence', 'title')
 * @param fromStyle - Optional. The input string's current style. Will be auto-detected if not provided
 * @returns The converted string in the target style
 *
 * @example
 * // Converting between different cases
 * convertTextStyle('camelCaseExample', 'kebab')     // -> 'camel-case-example'
 * convertTextStyle('PascalCaseExample', 'snake')    // -> 'pascal_case_example'
 * convertTextStyle('kebab-case-example', 'camel')   // -> 'kebabCaseExample'
 * convertTextStyle('snake_case_example', 'pascal')  // -> 'SnakeCaseExample'
 *
 * // Handling spaces and capitalization
 * convertTextStyle('sentence case example', 'camel') // -> 'sentenceCaseExample'
 * convertTextStyle('sentence case example', 'title') // -> 'Sentence Case Example'
 * convertTextStyle('HELLO WORLD', 'sentence')       // -> 'Hello world'
 */
export function convertTextStyle(
  input: string,
  toStyle: TextStyle,
  fromStyle?: TextStyle,
): string {
  function convertToStyle(text: string, style: TextStyle): string {
    if (style === 'title') {
      // Special handling for title case to preserve spaces, apostrophes, and abbreviations
      return text
        .split(/\s+/)
        .map((word) => {
          // Check if word is a known abbreviation
          if (ABBREVIATIONS.includes(word.toUpperCase())) {
            return word.toUpperCase()
          }

          // Handle apostrophes
          if (word.includes("'")) {
            const [first, ...rest] = word.split("'")
            return startCase(first) + "'" + rest.join("'").toLowerCase()
          }

          return startCase(word)
        })
        .join(' ')
    }

    // For other styles, first convert the text using Lodash
    let result: string
    switch (style) {
      case 'camel':
        result = camelCase(text)
        break
      case 'pascal':
        result = upperFirst(camelCase(text))
        break
      case 'kebab':
        result = kebabCase(text)
        break
      case 'snake':
        result = snakeCase(text)
        break
      case 'sentence':
        result =
          text.charAt(0).toUpperCase() + text.slice(1).toLowerCase().replace(/[_-]/g, ' ')
        break
      default:
        result = text
    }

    // Then restore any abbreviations that were converted
    ABBREVIATIONS.forEach((abbr) => {
      const lowerAbbr = abbr.toLowerCase()
      const regex = new RegExp(`\\b${lowerAbbr}\\b`, 'gi')
      result = result.replace(regex, abbr)
    })

    return result
  }

  // Handle all uppercase input
  if (isEmpty(input)) return ''

  const normalizedInput =
    input === input.toUpperCase()
      ? input.charAt(0).toUpperCase() + input.slice(1).toLowerCase()
      : input

  const style = fromStyle || detectTextStyle(normalizedInput)
  const normalizedText = convertToStyle(normalizedInput, style)
  return convertToStyle(normalizedText, toStyle)
}

/**
 * @method decodeHtmlEntities
 * @description Decodes HTML entities like &lt; and &gt; to < and >
 * @param text The text containing HTML entities
 * @returns The decoded text
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return text

  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

/**
 * @method formatCardNumber
 * @description Formats card number with spaces every 4 digits
 * @param {string} cardNumber - Card number string (e.g., "4012888888881881")
 * @returns {string} Formatted card number (e.g., "4012 8888 8888 1881")
 */
export function formatCardNumber(cardNumber: string | undefined): string {
  if (!cardNumber) return ''

  // Remove any existing spaces and non-digit characters
  const cleanNumber = cardNumber.replace(/\D/g, '')

  // Add space every 4 digits
  return cleanNumber.replace(/(\d{4})(?=\d)/g, '$1 ')
}

/**
 * @method isUrl
 * @param url {string}
 * @description Check the string is url or not
 */
export function isUrl(url: string): boolean {
  try {
    return Boolean(new URL(url))
  } catch {
    return false
  }
}
